'use client';

import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs';
import type { GenericBlok } from '@/components/customFields/types';
import NestedBlocks from '@/components/nestedBlocks/NestedBlocks';
import { replaceAssetUrl } from '@/helper/replaceAssetUrl';
import type { TrackingViewItemInformation } from '@/helper/trackingUtils';
import { appendHrefQuery, linkToString } from '@/helper/urls';
import { resolveStory } from '@/helper/utils';
import { useHeaderSync } from '@/hooks/useHeaderSync';
import { useKwkThankYouCta } from '@/hooks/useKwkThankYouCta';
import { usePurchaseTracking } from '@/hooks/usePurchaseTracking';
import { useViewItemTracking } from '@/hooks/useViewItemTracking';
import { useCalculatorStore } from '@/providers/CalculatorStoreProvider';
import { useHeaderStore } from '@/providers/HeaderStoreProvider';
import { useOrderFormSettings } from '@/providers/OrderFormSettingsProvider';
import { useOrderStore } from '@/providers/OrderStoreProvider';
import { PersonalizationProvider } from '@/providers/PersonalizationProvider';
import { useUserStore } from '@/providers/UserStoreProvider';
import type { PageContentStoryblok } from '@/types/component-types-sb';
import { type ISbStoryData, storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './PageContent.module.scss';

export function buildKwkCtaLink(
  kwkLandingPage: Parameters<typeof linkToString>[0],
  kwkCode: string | undefined
): string {
  const baseUrl = linkToString(kwkLandingPage);
  const params = new URLSearchParams();
  if (kwkCode) params.set('telljaid', kwkCode);
  const vkn = typeof window !== 'undefined' ? sessionStorage.getItem('swd_vkn') : null;
  if (vkn) params.set('swd_vkn', vkn);
  const campaign = typeof window !== 'undefined' ? sessionStorage.getItem('swd_campaign') : null;
  if (campaign) params.set('swd_campaign', campaign);
  const qs = params.toString();
  return qs ? appendHrefQuery(baseUrl, qs) : baseUrl;
}

export const PageContent: React.FC<{
  blok: PageContentStoryblok;
  breadcrumbs?: ISbStoryData<GenericBlok>[];
  trackingItem?: TrackingViewItemInformation | null;
  formSourceStoryUuid?: string;
}> = ({ blok, breadcrumbs, trackingItem, formSourceStoryUuid }) => {
  const { t } = useTranslation();
  const orderSettings = useOrderFormSettings();
  const path = usePathname();

  const {
    id,
    setMeta,
    reset,
    resetEdited,
    basicSupplyTrackingInformation,
    trackingInformation,
    isBasicSupply,
    tariff,
    selfServiceRegistration,
    personalData,
    isKwKOrder,
    kwkCode,
    incentive,
  } = useOrderStore((state) => ({
    id: state.id,
    setMeta: state.setMeta,
    reset: state.reset,
    resetEdited: state.resetEdited,
    basicSupplyTrackingInformation: state.basicSupplyTrackingInformation,
    trackingInformation: state.trackingInformation,
    isBasicSupply: state.isBasicSupply,
    tariff: state.tariff,
    selfServiceRegistration: state.selfServiceRegistration,
    personalData: state.personalData,
    isKwKOrder: state.isKwKOrder,
    kwkCode: state.kwkCode,
    incentive: state.incentive,
  }));

  const { resetSinglePriceCalculator } = useCalculatorStore((state) => ({
    resetSinglePriceCalculator: state.resetSinglePriceCalculator,
  }));

  const headerStore = useHeaderStore((state) => ({
    setDisplayAnchorNavigation: state.setDisplayAnchorNavigation,
    setAnchorList: state.setAnchorList,
    setCtaText: state.setCtaText,
    setCtaLink: state.setCtaLink,
    resetAnchorNavigationProps: state.resetAnchorNavigationProps,
  }));

  const userStore = useUserStore((state) => ({
    userTrackingData: state.trackingData,
    setUserTrackingData: state.setTrackingData,
  }));

  const thankYouPageUid = resolveStory<PageContentStoryblok>(orderSettings?.thank_you_page)?.content._uid;
  const basicSupplyThankYouUid = resolveStory<PageContentStoryblok>(orderSettings?.basic_supply_thankyou)?.content._uid;
  const currentPageUid = blok._uid;

  const [kwkThankYouCta, setKwkThankYouCta] = useState<{ link: string; text: string } | null>(null);

  useKwkThankYouCta({
    thankYouPageUid,
    currentPageUid,
    isKwKOrder,
    kwkCode,
    orderSettings,
    kwkThankYouCta,
    setKwkThankYouCta,
  });

  usePurchaseTracking({
    id,
    setMeta,
    reset,
    resetEdited,
    resetSinglePriceCalculator,
    basicSupplyTrackingInformation,
    trackingInformation,
    isBasicSupply,
    tariff,
    selfServiceRegistration,
    personalData,
    isKwKOrder,
    kwkCode,
    incentive,
    thankYouPageUid,
    basicSupplyThankYouUid,
    currentPageUid,
    orderSettings,
    setKwkThankYouCta,
  });

  useHeaderSync({ blok, path, headerStore });

  useViewItemTracking({ userStore, trackingItem });

  const withBreadcrumbs = blok.seo ? !!blok.seo[0]?.withBreadcrumbs : false;

  const stageBlocks =
    kwkThankYouCta && blok.stage?.[0] && blok.stage[0].component === 'component_stage'
      ? [{ ...blok.stage[0], ctaLink: kwkThankYouCta.link, ctaText: kwkThankYouCta.text }, ...blok.stage.slice(1)]
      : blok.stage;

  const styles: Record<string, string> = {};
  if (blok.backgroundImage) {
    styles['--page-content-background-image'] = `url(${replaceAssetUrl(blok.backgroundImage.filename)})`;
  }
  if (blok.backgroundSize) {
    styles['--page-content-background-size'] = blok.backgroundSize;
  }
  if (blok.backgroundRepeat) {
    styles['--page-content-background-repeat'] = blok.backgroundRepeat;
  }
  if (blok.backgroundPosition) {
    styles['--page-content-background-position'] = blok.backgroundPosition;
  }
  if (blok.backgroundColor) {
    styles['--page-content-background-color'] = blok.backgroundColor;
  }

  return (
    <>
      <div {...storyblokEditable(blok)} className={classNames(classes.pageContent, 'pageContent')} style={styles}>
        <PersonalizationProvider activeUserSegments={blok.active_user_segments}>
          <NestedBlocks blocks={stageBlocks} formSourceStoryUuid={formSourceStoryUuid} />
          <main className="grid-wrapper" aria-label={t('main')}>
            {withBreadcrumbs && <Breadcrumbs stories={breadcrumbs} />}
            <div className="content-bloks-wrapper">
              <NestedBlocks blocks={blok.content} formSourceStoryUuid={formSourceStoryUuid} />
            </div>
          </main>
        </PersonalizationProvider>
      </div>
    </>
  );
};
export default PageContent;
